import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  uploadFile,
  resendInitialPasswords,
  clearResendState,
  selectResendLoading,
  selectResendResult,
  selectResendError,
  clearMessages,
} from "../../redux/cargaMasivaSlice";
import {
  fetchRegiones,
  fetchComunasByRegion,
} from "../../redux/regionComunaSlice";
import FileUpload from "../../components/FileUpload";
import Notification from "../../components/Notification";
import {
  Button,
  Container,
  Row,
  Col,
  Table,
  Modal,
  Form,
  Spinner,
  Card,
  Alert,
} from "react-bootstrap";
import Swal from "sweetalert2";
import { ProgressBar } from "react-bootstrap";

// --- Util: decodificar JWT sin libs (solo para UI) ---
function parseJwt(token) {
  try {
    if (!token || typeof token !== "string" || token.split(".").length < 2)
      return null;
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (_e) {
    return null;
  }
}

const CargaMasivaUsuariosPage = () => {
  const dispatch = useDispatch();

  // --- AUTH ---
  const auth = useSelector((state) => state.auth);
  const token =
    auth?.token ||
    (typeof window !== "undefined" ? localStorage.getItem("token") : null);
  const user = auth?.user || auth?.profile || null;
  const roleFromUser =
    user?.rol || user?.role || user?.Rol || user?.ROLE || undefined;
  const flagAdminFromUser =
    user?.isAdmin ||
    user?.is_admin ||
    user?.admin ||
    (typeof window !== "undefined" &&
      localStorage.getItem("isAdmin") === "true");

  // Fallback: role desde localStorage.user si existe
  const lsUserRole = useMemo(() => {
    try {
      const raw =
        typeof window !== "undefined" ? localStorage.getItem("user") : null;
      if (!raw) return undefined;
      const parsed = JSON.parse(raw);
      return parsed?.rol || parsed?.role || parsed?.Rol || parsed?.ROLE;
    } catch (_e) {
      return undefined;
    }
  }, []);

  // JWT payload memoizado
  const jwtPayload = useMemo(() => parseJwt(token), [token]);

  // Roles provenientes de JWT, memoizados aparte (para satisfacer eslint exhaustive-deps)
  const rolesFromJwt = useMemo(() => {
    if (!jwtPayload) return [];
    if (Array.isArray(jwtPayload.roles)) return jwtPayload.roles;
    if (jwtPayload.role) return [jwtPayload.role];
    if (jwtPayload.authorities)
      return Array.isArray(jwtPayload.authorities)
        ? jwtPayload.authorities
        : [jwtPayload.authorities];
    return [];
  }, [jwtPayload]);

  // Deducción de admin (solo para mostrar UI)
  const isAdmin = useMemo(() => {
    if (flagAdminFromUser) return true;
    const candidates = [roleFromUser, lsUserRole]
      .filter(Boolean)
      .map((r) => String(r).toLowerCase());
    if (candidates.some((r) => r === "admin" || r === "administrador"))
      return true;
    const lowered = rolesFromJwt.map((r) => String(r).toLowerCase());
    if (lowered.some((r) => r.includes("admin") || r.includes("administrador")))
      return true;
    return false;
  }, [flagAdminFromUser, roleFromUser, lsUserRole, rolesFromJwt]);

  // --- SLICE STATE (cargaMasiva) ---
  const { loading, success, error, resultados } = useSelector(
    (state) => state.cargaMasiva
  );

  // --- REGION/COMUNA STATE ---
  const {
    regiones,
    comunas,
    loading: loadingRegiones,
  } = useSelector((state) => state.regionComuna);

  // --- LOCAL UI STATE ---
  const [selectedFile, setSelectedFile] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedComuna, setSelectedComuna] = useState(null);
  const [, setErroresTexto] = useState("");
  const [showErroresDetalles, setShowErroresDetalles] = useState(false);
  const [progress, setProgress] = useState(0);
  const [processingTime, setProcessingTime] = useState("");

  // --- RESEND SELECTORS ---
  const resendLoading = useSelector(selectResendLoading);
  const resendResult = useSelector(selectResendResult);
  const resendError = useSelector(selectResendError);

  const clearResend = () => {
    dispatch(clearResendState());
  };

  const handleResend = () => {
    dispatch(resendInitialPasswords());
  };

  // --- EFFECTS ---
  useEffect(() => {
    dispatch(fetchRegiones());
    return () => {
      dispatch(clearMessages());
    };
  }, [dispatch]);

  useEffect(() => {
    if (selectedRegion) {
      dispatch(fetchComunasByRegion(selectedRegion));
    } else {
      setSelectedComuna(null);
    }
  }, [selectedRegion, dispatch]);

  useEffect(() => {
    if (success || error) {
      setShowModal(true);
      if (success && resultados) {
        let mensajeExito = `¡Carga masiva completada!\\n\\n`;
        mensajeExito += `Total registros procesados: ${
          resultados.totalRegistros || 0
        }\\n`;
        mensajeExito += `Registros exitosos: ${resultados.exitosos || 0}\\n`;
        mensajeExito += `Errores encontrados: ${resultados.fallidos || 0}\\n`;
        mensajeExito += `Tiempo de procesamiento: ${
          resultados.tiempoProcesamiento || "N/A"
        }`;

        setErroresTexto(mensajeExito);

        if (resultados.errores?.length > 0) {
          const erroresFormateados = resultados.errores
            .map(
              (err, index) =>
                `Error ${index + 1} - Fila ${err.fila}: ${err.error} (${
                  err.columna
                })`
            )
            .join("\\n");
          setErroresTexto(
            (prev) =>
              prev + "\\n\\nDetalles de errores:\\n" + erroresFormateados
          );
        }
      }

      if (error) {
        let errorMsg = "Error en la carga masiva";
        if (typeof error === "string") {
          errorMsg = error;
        } else if (error.message) {
          errorMsg = error.message;
        } else if (error.error) {
          errorMsg = error.error;
        }
        setErroresTexto(errorMsg);
      }
    }
  }, [success, error, resultados]);

  const handleUpload = async () => {
    if (!selectedFile) {
      Swal.fire("Error", "Seleccione un archivo primero", "warning");
      return;
    }

    const validExtensions = ["xlsx", "xls"];
    const fileExtension = selectedFile.name.split(".").pop().toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      Swal.fire(
        "Error",
        "Formato no válido. Use archivos Excel (.xlsx, .xls)",
        "error"
      );
      return;
    }

    const { isConfirmed } = await Swal.fire({
      title: "Confirmar carga masiva",
      text: `¿Está seguro de cargar ${selectedFile.name}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, continuar",
      cancelButtonText: "Cancelar",
    });

    if (!isConfirmed) return;

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev + Math.random() * 5;
        return newProgress > 90 ? 90 : newProgress;
      });
    }, 500);

    try {
      setErroresTexto("");
      setProcessingTime("");
      setProgress(10);

      const startTime = Date.now();
      const result = await dispatch(uploadFile(selectedFile)).unwrap();
      const endTime = Date.now();
      const seconds = ((endTime - startTime) / 1000).toFixed(2);
      setProcessingTime(`Procesado en ${seconds} segundos`);

      if (result?.errores?.length > 0) {
        const erroresDetallados = result.errores
          .map((err) => {
            return `Fila ${err.fila} (${err.columna}): ${err.error}\\nValor: ${
              err.valor || "N/A"
            }`;
          })
          .join("\\n\\n");

        setErroresTexto(erroresDetallados);
      }

      setProgress(100);
      setTimeout(() => setProgress(0), 2000);
    } catch (error) {
      setProgress(0);
      console.error("Error en la carga:", error);
    } finally {
      clearInterval(progressInterval);
    }
  };

  return (
    <Container className="mt-4 mb-5">
      <h2 className="text-center mb-4">Carga Masiva de Usuarios</h2>

      {/* Sección de Instrucciones */}
      <Card className="mb-4">
        <Card.Header as="h5" className="bg-primary text-white">
          <i className="bi bi-info-circle me-2"></i>
          Instrucciones para la carga masiva
        </Card.Header>
        <Card.Body>
          <p className="lead">
            Sigue estos pasos para realizar una carga masiva de usuarios:
          </p>

          <ol>
            <li className="mb-2">
              <strong>Descarga la plantilla de ejemplo</strong> y completa los
              datos según el formato requerido.
            </li>
            <li className="mb-2">
              <strong>Verifica los IDs de comuna</strong> usando el buscador que
              encontrarás más abajo.
            </li>
            <li className="mb-2">
              <strong>Sube el archivo Excel</strong> completado usando el botón
              de carga.
            </li>
            <li>
              <strong>Revisa los resultados</strong> que se mostrarán después
              del procesamiento.
            </li>
          </ol>

          <div className="alert alert-info mt-3">
            <p className="mb-1">
              <strong>Importante:</strong>
            </p>
            <ul className="mb-1">
              <li>El archivo debe estar en formato Excel (.xlsx o .xls)</li>
              <li>
                La primera fila debe contener los encabezados exactos como se
                muestran en el ejemplo
              </li>
              <li>
                El campo <code>comuna_id</code> debe coincidir exactamente con
                el ID de la comuna
              </li>
            </ul>
          </div>
        </Card.Body>
      </Card>

      {/* Sección de Formato y Ejemplo */}
      <Card className="mb-4">
        <Card.Header as="h5" className="bg-primary text-white">
          <i className="bi bi-file-earmark-spreadsheet me-2"></i>
          Formato del Archivo Excel
        </Card.Header>
        <Card.Body>
          <p>
            El archivo Excel debe tener los siguientes encabezados en la primera
            fila:
          </p>

          <div className="d-block d-sm-none alert alert-warning mb-3">
            <i className="bi bi-phone me-2"></i>
            En dispositivos móviles algunas columnas están ocultas. Gira tu
            dispositivo o desliza horizontalmente para ver más información.
          </div>

          <div className="table-responsive">
            <Table striped bordered className="small">
              <thead>
                <tr>
                  <th className="text-nowrap">nombre</th>
                  <th className="text-nowrap d-none d-sm-table-cell">
                    primer_apellido
                  </th>
                  <th className="text-nowrap d-none d-md-table-cell">
                    segundo_apellido
                  </th>
                  <th className="text-nowrap">rut</th>
                  <th className="text-nowrap">dv</th>
                  <th className="text-nowrap d-none d-sm-table-cell">email</th>
                  <th className="text-nowrap d-none d-md-table-cell">
                    teléfono
                  </th>
                  <th className="text-nowrap d-none d-lg-table-cell">
                    fecha_nac.
                  </th>
                  <th className="text-nowrap d-none d-lg-table-cell">
                    empresa
                  </th>
                  <th className="text-nowrap">comuna_id</th>
                  <th className="text-nowrap">rol</th>
                  <th className="text-nowrap d-none d-md-table-cell">
                    fecha_ingreso
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Juan</td>
                  <td className="d-none d-sm-table-cell">Pérez</td>
                  <td className="d-none d-md-table-cell">González</td>
                  <td>12345678</td>
                  <td>9</td>
                  <td className="d-none d-sm-table-cell">juan@test.com</td>
                  <td className="d-none d-md-table-cell">+56912345678</td>
                  <td className="d-none d-lg-table-cell">1990-05-10</td>
                  <td className="d-none d-lg-table-cell">claro-vtr</td>
                  <td>121</td>
                  <td>socio</td>
                  <td className="d-none d-md-table-cell">
                    2023-01-15 (opcional)
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>

          <div className="alert alert-warning mt-3">
            <p className="mb-1">
              <strong>Notas sobre el formato:</strong>
            </p>
            <ul className="mb-1">
              <li>
                <code>comuna_id</code>: Debe ser el ID numérico de la comuna
                (ver buscador abajo)
              </li>
              <li>
                <code>fecha_ingreso</code>: Opcional. Si no se especifica, se
                usará la fecha actual
              </li>
              <li>
                Formatos de fecha: <code>YYYY-MM-DD</code> (ej: 2023-12-31)
              </li>
              <li>
                Valores de rol permitidos: <code>administrador</code>,{" "}
                <code>socio</code>
              </li>
              <li>RUT: Solo números (sin puntos ni guión)</li>
            </ul>
          </div>

          <div className="text-center mt-4">
            <a
              href="https://api.sinterec.cl/public/docs/FormatoCargaMasiva.xlsx"
              download="FormatoCargaMasiva.xlsx"
              className="btn btn-success btn-lg"
            >
              <i className="bi bi-download me-2"></i> Descargar Plantilla de
              Ejemplo
            </a>
          </div>
        </Card.Body>
      </Card>

      {/* Sección de Buscador de Comunas */}
      <Card className="mb-4">
        <Card.Header as="h5" className="bg-primary text-white">
          <i className="bi bi-search me-2"></i>
          Buscador de IDs de Comuna
        </Card.Header>
        <Card.Body>
          <p className="lead">
            Utiliza este buscador para encontrar el <code>comuna_id</code>{" "}
            correcto que debes incluir en tu archivo Excel.
          </p>

          <Row className="justify-content-center">
            <Col md={8}>
              <Form.Group className="mb-3">
                <Form.Label>Selecciona una Región:</Form.Label>
                <Form.Control
                  as="select"
                  value={selectedRegion || ""}
                  onChange={(e) => {
                    const regionId = e.target.value
                      ? parseInt(e.target.value)
                      : null;
                    setSelectedRegion(regionId);
                    setSelectedComuna(null);
                  }}
                  disabled={loadingRegiones}
                >
                  <option value="">Seleccione una Región</option>
                  {regiones.map((region) => (
                    <option key={region.id} value={region.id}>
                      {region.nombre}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Selecciona una Comuna:</Form.Label>
                <Form.Control
                  as="select"
                  value={selectedComuna || ""}
                  onChange={(e) => {
                    const comunaId = e.target.value
                      ? parseInt(e.target.value)
                      : null;
                    setSelectedComuna(comunaId);
                  }}
                  disabled={!selectedRegion || loadingRegiones}
                >
                  <option value="">Seleccione una Comuna</option>
                  {comunas.map((comuna) => (
                    <option key={comuna.id} value={comuna.id}>
                      {comuna.nombre} (ID: {comuna.id})
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>

              {selectedComuna && (
                <div className="alert alert-success mt-3">
                  <h6>ID de Comuna seleccionado:</h6>
                  <p className="display-6 text-center mb-0">
                    <code>{selectedComuna}</code>
                  </p>
                  <p className="text-center mt-2">
                    <small>
                      Usa este valor en la columna <code>comuna_id</code> de tu
                      archivo Excel
                    </small>
                  </p>
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Sección de Carga de Archivo */}
      <Card className="mb-4">
        <Card.Header as="h5" className="bg-primary text-white">
          <i className="bi bi-cloud-upload me-2"></i>
          Subir Archivo Excel
        </Card.Header>
        <Card.Body>
          <Row className="justify-content-center">
            <Col md={8} className="text-center">
              <Notification message={error} type="danger" />
              <Notification message={success} type="success" />

              <div className="mb-4">
                <FileUpload onFileSelect={setSelectedFile} />
                {selectedFile && (
                  <div className="mt-2">
                    <p className="text-success">
                      <i className="bi bi-file-earmark-excel-fill me-2"></i>
                      Archivo seleccionado: <strong>{selectedFile.name}</strong>
                    </p>
                    <p className="text-muted small">
                      Tamaño: {(selectedFile.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                )}
              </div>

              <Button
                variant={selectedFile ? "success" : "primary"}
                size="lg"
                onClick={handleUpload}
                disabled={loading || !selectedFile}
                className="mb-3"
              >
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Procesando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-upload me-2"></i>
                    {selectedFile
                      ? `Subir ${selectedFile.name}`
                      : "Selecciona un archivo"}
                  </>
                )}
              </Button>

              {/* DEBUG UI */}
               {/*  <div className="mt-3" style={{ fontSize: 12, color: "#666" }}>
                isAdmin (UI): <code>{String(isAdmin)}</code> | role(user):{" "}
                <code>{String(roleFromUser ?? "—")}</code> | token?{" "}
                <code>{String(!!token)}</code>
              </div> */}

              {/* BLOQUE REENVÍO SIEMPRE VISIBLE; BACKEND HARÁ LA AUTORIZACIÓN */}
              <div className="card mt-4">
                <div className="card-body">
                  <h5 className="card-title">
                    Reenviar credenciales de primer acceso
                  </h5>
                  {!isAdmin && (
                    <Alert variant="secondary">
                      <h3>
                        Esta acción es para reenviar credenciales de acceso a
                        los socios que no hayan ingresado a la aplicación ni
                        completado sus datos.
                      </h3>
                      <strong>
                        Tu sesión no muestra rol de administrador en el
                        frontend. De todos modos, el backend validará tu token y
                        rol
                      </strong>{" "}
                      al ejecutar la acción.
                    </Alert>
                  )}
                  {!token && (
                    <Alert variant="warning">
                      No hay token disponible. Inicia sesión nuevamente.
                    </Alert>
                  )}
                  <p className="card-text">
                    Esta acción reasigna una clave temporal y envía un correo a
                    cada usuario cuya <code>contacto_emergencia</code> y{" "}
                    <code>telefono_emergencia</code> estén{" "}
                    <strong>vacíos</strong>.
                  </p>

                  <button
                    className="btn btn-danger"
                    onClick={handleResend}
                    disabled={resendLoading || !token}
                    aria-busy={resendLoading ? "true" : "false"}
                  >
                    {resendLoading
                      ? "Procesando…"
                      : "Reenviar claves temporales"}
                  </button>

                  {/* Mensajes */}
                  {resendError && (
                    <div className="alert alert-danger mt-3" role="alert">
                      {resendError}
                      <button
                        type="button"
                        className="btn btn-link btn-sm"
                        onClick={clearResend}
                        style={{ textDecoration: "none" }}
                      >
                        Cerrar
                      </button>
                    </div>
                  )}

                  {resendResult && (
                    <div className="alert alert-success mt-3" role="alert">
                      <div>
                        <strong>Total:</strong> {resendResult.total}
                      </div>
                      <div>
                        <strong>Exitosos:</strong> {resendResult.ok}
                      </div>
                      <div>
                        <strong>Fallidos:</strong> {resendResult.fail}
                      </div>

                      {Array.isArray(resendResult.errores) &&
                        resendResult.errores.length > 0 && (
                          <details className="mt-2">
                            <summary>
                              Ver errores ({resendResult.errores.length})
                            </summary>
                            <ul className="mt-2 mb-0">
                              {resendResult.errores
                                .slice(0, 50)
                                .map((e, idx) => (
                                  <li key={idx}>
                                    <code>{e.email || e.usuario || "-"}</code>:{" "}
                                    {e.error || e.mensaje || "Error"}
                                  </li>
                                ))}
                              {resendResult.errores.length > 50 && (
                                <li>
                                  … y {resendResult.errores.length - 50} más
                                </li>
                              )}
                            </ul>
                          </details>
                        )}

                      <div className="mt-2">
                        <button
                          type="button"
                          className="btn btn-link btn-sm"
                          onClick={clearResend}
                          style={{ textDecoration: "none" }}
                        >
                          Cerrar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {loading && (
                <div className="mt-3">
                  <div className="d-flex justify-content-between mb-1">
                    <small>Progreso: {Math.round(progress)}%</small>
                    {processingTime && <small>{processingTime}</small>}
                  </div>
                  <ProgressBar
                    now={progress}
                    animated
                    striped
                    variant={progress < 100 ? "danger" : "success"}
                    label={`${Math.round(progress)}%`}
                  />
                  <div className="text-center mt-2">
                    <small>Procesando archivo, por favor espere...</small>
                  </div>
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Modal con resultados */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Resultados de la Carga</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading && <p>Subiendo archivo, por favor espera...</p>}

          {resultados?.usuariosRegistrados?.length > 0 && (
            <>
              <h5>Usuarios Registrados</h5>
              <div className="table-responsive">
                <Table striped bordered hover size="sm" className="mb-4">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Nombre</th>
                      <th className="d-none d-sm-table-cell">
                        Primer Apellido
                      </th>
                      <th className="d-none d-md-table-cell">
                        Segundo Apellido
                      </th>
                      <th className="text-nowrap">Email</th>
                      <th className="d-none d-sm-table-cell">Empresa</th>
                      <th>Comuna</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultados.usuariosRegistrados.map((usuario, index) => {
                      const comuna = comunas.find(
                        (c) => c.id === usuario.comuna_id
                      );
                      return (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td
                            title={usuario.nombre}
                            className="text-truncate"
                            style={{ maxWidth: "100px" }}
                          >
                            {usuario.nombre}
                          </td>
                          <td
                            title={usuario.primer_apellido}
                            className="d-none d-sm-table-cell text-truncate"
                            style={{ maxWidth: "100px" }}
                          >
                            {usuario.primer_apellido}
                          </td>
                          <td
                            title={usuario.segundo_apellido}
                            className="d-none d-md-table-cell text-truncate"
                            style={{ maxWidth: "100px" }}
                          >
                            {usuario.segundo_apellido}
                          </td>
                          <td
                            title={usuario.email}
                            className="text-truncate"
                            style={{ maxWidth: "150px" }}
                          >
                            {usuario.email}
                          </td>
                          <td
                            title={usuario.empresa}
                            className="d-none d-sm-table-cell text-truncate"
                            style={{ maxWidth: "100px" }}
                          >
                            {usuario.empresa}
                          </td>
                          <td>{comuna ? comuna.nombre : "Desconocida"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </>
          )}

          {resultados?.errores?.length > 0 && (
            <>
              <h5 className="text-danger">Errores de Validación</h5>
              <p>
                Se encontraron {resultados.errores.length} errores en{" "}
                {resultados.totalRegistros} registros
              </p>

              <div className="mb-3">
                <strong>Resumen:</strong>
                <ul>
                  {resultados.errores.slice(0, 5).map((err, idx) => (
                    <li key={idx}>
                      Fila {err.fila}: {err.error} ({err.columna})
                    </li>
                  ))}
                </ul>
                {resultados.errores.length > 5 && (
                  <p>+ {resultados.errores.length - 5} errores más...</p>
                )}
              </div>

              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => setShowErroresDetalles(!showErroresDetalles)}
              >
                {showErroresDetalles
                  ? "Ocultar detalles"
                  : "Ver todos los errores"}
              </Button>

              {showErroresDetalles && (
                <div className="mt-3">
                  <Table striped bordered size="sm">
                    <thead>
                      <tr>
                        <th>Fila</th>
                        <th>Campo</th>
                        <th>Error</th>
                        <th>Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultados.errores.map((err, idx) => (
                        <tr key={idx}>
                          <td>{err.fila}</td>
                          <td>{err.columna}</td>
                          <td className="text-danger">{err.error}</td>
                          <td
                            className="text-truncate"
                            style={{ maxWidth: "150px" }}
                          >
                            {err.valor}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </>
          )}

          {success && resultados?.usuariosRegistrados?.length === 0 && (
            <p className="text-info">
              No se registraron usuarios. Revise los errores para más detalles.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default CargaMasivaUsuariosPage;
